import { fetchAuthSession } from '@aws-amplify/auth';
import { BACKEND_AGENTS_API, BACKEND_SEMANTIC_MODEL_API, BACKEND_ICEBERG_DATABASES_API, BACKEND_ICEBERG_SCHEMAS_API, BACKEND_ICEBERG_TABLES_API } from '../env';

// Fase 5.1: el historial de snapshots vive en whisperbi-agent-management-strands
// (IcebergSnapshotsFunction), no en whisperbi-back-semantic-model como el resto
// de este servicio -- reutiliza Athena/el WorkGroup que ya existen ahí, en vez
// de sumarle esa infraestructura a semantic-model.
const SNAPSHOTS_API_BASE = `${BACKEND_AGENTS_API}/iceberg-tables/snapshots`;

async function getAuthHeader() {
  try {
    const tokens = (await fetchAuthSession()).tokens;
    const idToken = tokens?.idToken?.toString();
    if (idToken) {
      return { Authorization: `Bearer ${idToken}` };
    }
  } catch (error) {
    console.error('IcebergCatalogService.getAuthHeader: unable to fetch session', error);
  }
  return {};
}

async function fetchWithAuth(input, init = {}) {
  const authHeader = await getAuthHeader();
  const headers = {
    ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    ...(init.headers || {}),
    ...authHeader
  };

  const response = await fetch(input, { ...init, headers, mode: 'cors', cache: 'no-cache' });

  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${errorBody || 'Unexpected error'}`);
  }
  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return text || null;
  }
}

/**
 * Cliente del módulo Catálogo Iceberg S3 (Fase 1). Jerarquía Database ->
 * Schema (máx. 100 por database, aplicado server-side) -> Table. Table es
 * de solo lectura en esta fase -- las tablas se crean hoy desde
 * file-ingestion (subida de Excel/CSV), no desde este módulo; eso llega en
 * una fase posterior del plan "Catálogo Iceberg S3".
 */
const IcebergCatalogService = {
  // ---- Mantenimiento automático (informe) ----------------------------
  // Estado del mantenimiento de las tablas del tenant del usuario, por
  // Base de Datos -> Esquema -> Tabla. Sin account_id a propósito: el
  // backend lo toma SOLO del token (cada tenant ve únicamente lo suyo).
  async getMaintenanceReport() {
    return fetchWithAuth(`${BACKEND_SEMANTIC_MODEL_API}/iceberg-maintenance`, { method: 'GET' });
  },

  // ---- Databases -----------------------------------------------------
  async listDatabases(accountId) {
    if (!accountId) {
      throw new Error('IcebergCatalogService.listDatabases: accountId is required');
    }
    const params = new URLSearchParams({ account_id: accountId });
    return fetchWithAuth(`${BACKEND_ICEBERG_DATABASES_API}?${params.toString()}`, { method: 'GET' });
  },

  async getDatabase(accountId, icebergDatabaseId) {
    if (!accountId || !icebergDatabaseId) {
      throw new Error('IcebergCatalogService.getDatabase: accountId and icebergDatabaseId are required');
    }
    const url = `${BACKEND_ICEBERG_DATABASES_API}/${encodeURIComponent(accountId)}/${encodeURIComponent(icebergDatabaseId)}`;
    return fetchWithAuth(url, { method: 'GET' });
  },

  async createDatabase({ name, description }) {
    if (!name) {
      throw new Error('IcebergCatalogService.createDatabase: name is required');
    }
    return fetchWithAuth(BACKEND_ICEBERG_DATABASES_API, {
      method: 'POST',
      body: JSON.stringify({ name, description })
    });
  },

  async updateDatabase(payload) {
    if (!payload?.iceberg_database_id) {
      throw new Error('IcebergCatalogService.updateDatabase: iceberg_database_id is required in payload');
    }
    return fetchWithAuth(BACKEND_ICEBERG_DATABASES_API, { method: 'PUT', body: JSON.stringify(payload) });
  },

  async softDeleteDatabase(icebergDatabaseId, { accountId } = {}) {
    if (!icebergDatabaseId) {
      throw new Error('IcebergCatalogService.softDeleteDatabase: icebergDatabaseId is required');
    }
    const params = new URLSearchParams({ iceberg_database_id: icebergDatabaseId });
    // Necesario para root (puede operar sobre otro tenant); el backend lo
    // ignora para el resto de los usuarios.
    if (accountId) {
      params.set('account_id', accountId);
    }
    return fetchWithAuth(`${BACKEND_ICEBERG_DATABASES_API}/soft?${params.toString()}`, { method: 'DELETE' });
  },

  // ---- Schemas ---------------------------------------------------------
  async listSchemas(accountId, { icebergDatabaseId } = {}) {
    if (!accountId) {
      throw new Error('IcebergCatalogService.listSchemas: accountId is required');
    }
    const params = new URLSearchParams({ account_id: accountId });
    if (icebergDatabaseId) {
      params.set('iceberg_database_id', icebergDatabaseId);
    }
    return fetchWithAuth(`${BACKEND_ICEBERG_SCHEMAS_API}?${params.toString()}`, { method: 'GET' });
  },

  async getSchema(accountId, icebergSchemaId) {
    if (!accountId || !icebergSchemaId) {
      throw new Error('IcebergCatalogService.getSchema: accountId and icebergSchemaId are required');
    }
    const url = `${BACKEND_ICEBERG_SCHEMAS_API}/${encodeURIComponent(accountId)}/${encodeURIComponent(icebergSchemaId)}`;
    return fetchWithAuth(url, { method: 'GET' });
  },

  async createSchema({ icebergDatabaseId, name, description }) {
    if (!icebergDatabaseId || !name) {
      throw new Error('IcebergCatalogService.createSchema: icebergDatabaseId and name are required');
    }
    return fetchWithAuth(BACKEND_ICEBERG_SCHEMAS_API, {
      method: 'POST',
      body: JSON.stringify({ iceberg_database_id: icebergDatabaseId, name, description })
    });
  },

  async updateSchema(payload) {
    if (!payload?.iceberg_schema_id) {
      throw new Error('IcebergCatalogService.updateSchema: iceberg_schema_id is required in payload');
    }
    return fetchWithAuth(BACKEND_ICEBERG_SCHEMAS_API, { method: 'PUT', body: JSON.stringify(payload) });
  },

  async softDeleteSchema(icebergSchemaId, { accountId } = {}) {
    if (!icebergSchemaId) {
      throw new Error('IcebergCatalogService.softDeleteSchema: icebergSchemaId is required');
    }
    const params = new URLSearchParams({ iceberg_schema_id: icebergSchemaId });
    if (accountId) {
      params.set('account_id', accountId);
    }
    return fetchWithAuth(`${BACKEND_ICEBERG_SCHEMAS_API}/soft?${params.toString()}`, { method: 'DELETE' });
  },

  // ---- Tables (solo lectura en Fase 1) ---------------------------------
  async listTables(icebergSchemaId, { accountId } = {}) {
    if (!icebergSchemaId || !accountId) {
      throw new Error('IcebergCatalogService.listTables: icebergSchemaId and accountId are required');
    }
    // account_id va en el path (no como query param) -- mismo criterio que
    // el resto de las rutas de este servicio (getTable, resolveSchemaReview,
    // cloneTable). Alineado en el backend el 2026-09-24: API Gateway exige
    // que todas las rutas bajo /iceberg-schemas/{...} usen el mismo nombre
    // de variable en esa posición del árbol.
    const url = `${BACKEND_ICEBERG_SCHEMAS_API}/${encodeURIComponent(accountId)}/${encodeURIComponent(icebergSchemaId)}/tables`;
    return fetchWithAuth(url, { method: 'GET' });
  },

  async getTable(accountId, icebergTableId) {
    if (!accountId || !icebergTableId) {
      throw new Error('IcebergCatalogService.getTable: accountId and icebergTableId are required');
    }
    const url = `${BACKEND_ICEBERG_TABLES_API}/${encodeURIComponent(accountId)}/${encodeURIComponent(icebergTableId)}`;
    return fetchWithAuth(url, { method: 'GET' });
  },

  // ---- Snapshots (Fase 5.1, "time travel" en UI) -----------------------
  async getSnapshots(glueDatabase, tableName) {
    if (!glueDatabase || !tableName) {
      throw new Error('IcebergCatalogService.getSnapshots: glueDatabase and tableName are required');
    }
    const params = new URLSearchParams({ glue_database: glueDatabase, table_name: tableName });
    return fetchWithAuth(`${SNAPSHOTS_API_BASE}?${params.toString()}`, { method: 'GET' });
  },

  // ---- Schema evolution asistido (Fase 5.2) -----------------------------
  async resolveSchemaReview(accountId, icebergTableId, decision) {
    if (!accountId || !icebergTableId || !decision) {
      throw new Error('IcebergCatalogService.resolveSchemaReview: accountId, icebergTableId and decision are required');
    }
    const url = `${BACKEND_ICEBERG_TABLES_API}/${encodeURIComponent(accountId)}/${encodeURIComponent(icebergTableId)}/schema-review`;
    return fetchWithAuth(url, { method: 'POST', body: JSON.stringify({ decision }) });
  },

  // ---- Full Clone (Fase 5.3) --------------------------------------------
  // Copia COMPLETA de los datos de una tabla a una tabla Iceberg nueva --
  // NUNCA un zero-copy clone (Iceberg no lo ofrece vía Spark SQL simple).
  async cloneTable(accountId, icebergTableId, { targetIcebergSchemaId, targetTableName, snapshotId } = {}) {
    if (!accountId || !icebergTableId || !targetIcebergSchemaId || !targetTableName) {
      throw new Error(
        'IcebergCatalogService.cloneTable: accountId, icebergTableId, targetIcebergSchemaId and targetTableName are required'
      );
    }
    const url = `${BACKEND_ICEBERG_TABLES_API}/${encodeURIComponent(accountId)}/${encodeURIComponent(icebergTableId)}/clone`;
    const body = { target_iceberg_schema_id: targetIcebergSchemaId, target_table_name: targetTableName };
    if (snapshotId) {
      body.snapshot_id = snapshotId;
    }
    return fetchWithAuth(url, { method: 'POST', body: JSON.stringify(body) });
  }
};

export default IcebergCatalogService;
